import moment from "moment";
import { ChangeEvent, FC, useState, useEffect } from "react";
import { Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { kickoffMeeting } from "../../api/common";
import SubmitButton from "../../Components/Button";
import LoadingOverlay from "../../Components/LoadingOverlay";
import TextInput from "../../Components/TextInput";
import styles from "./style.module.scss";
import Alert from "../../Components/AlertPopup";
import Audit from "../../icons/Audit.svg?react";
import MeetingTimePicker from "../../Components/MeetingTimePicker";
import { getTodayISODate } from "../../utils";
import { UserState } from "../../reducers/userSlice";
import Tick from "../../icons/tick.svg?react";

interface AuditInfo {
  id: string;
  auditCode: string;
}

interface StatePorps {
  onClose: (data: any) => void;
  showModal?: boolean;
  userInfo?: UserState;
  auditId: AuditInfo;
  tableReload: () => void;
}

const SendKickoff: FC<StatePorps> = ({
  showModal = false,
  userInfo,
  onClose,
  auditId,
  tableReload,
}) => {

  const defaultMessage = `
Dear Team,

You are invited to the Kickoff Meeting for Audit ID: ${auditId?.auditCode ?? "[Audit ID]"}.

The purpose of this meeting is to introduce the audit objectives, discuss the scope, responsibilities, timelines, and clarify any preliminary queries before commencement.

Please make sure to join on time to ensure a smooth discussion.

Best regards,
${userInfo?.name ?? "A360"}
  `.trim();
  const [showToast, setShowToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [validated, setValidated] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [kickoffDate, setKickoffDate] = useState<undefined | string | number>(getTodayISODate());
  const [subject, setSubject] = useState<undefined | string>();
  const [fromTime, setFromTime] = useState<undefined | string>();
  const [toTime, setToTime] = useState<undefined | string>();
  const [messageBody, setMessageBody] = useState<any>();

  useEffect(() => {
    setSubject(`Kickoff Meeting for ${auditId?.auditCode}`)
    setMessageBody(defaultMessage);
    setKickoffDate(getTodayISODate());
  }, [showModal, auditId?.auditCode])
  const resetForm = () => {
    setValidated(false);
    setKickoffDate(undefined);
    setSubject(undefined);
    setFromTime(undefined);
    setToTime(undefined);
    setMessageBody(undefined);
  };

  const onCloseClick = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(false);
    onClose(false);
  };

  const handleClose = () => {
    setShowToast(false);
    setValidated(false);
    onClose(false);
  };

  const handleErrorClose = () => setShowErrorToast(false);

  const handleSubmit = async (event: ChangeEvent<HTMLFormElement>) => {
    const form = event.currentTarget;

    if (form.checkValidity() === true) {
      const formData = {
        kickoffDate,
        fromTime,
        toTime,
        subject,
        messageBody: messageBody?.replace(/\n/g, "<br>") + `<br><br><p class="text-muted">Note: This is an autogenerated email, please do not reply.</p>`,
        auditingId: auditId?.id,
      };
      setLoading(true);
      kickoffMeeting(formData)
        .then((response) => {
          setShowToast(true);
          setLoading(false);
          tableReload();
        })
        .catch((err) => {
          setLoading(false);
          setShowErrorToast(true);
        });
    }

    setValidated(true);
    event.preventDefault();
    event.stopPropagation();
  };
  const now = moment();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const auditCode = ` Kickoff Meeting for ${auditId?.auditCode ?? "-"}`;
    const value = e.target.value;

    // Prevent deletion of auditCode prefix
    if (!value.startsWith(auditCode)) {
      setSubject(auditCode); // reset to auditCode if they try to delete
    } else {
      setSubject(value);
    }
  };
  return (
    <>
      <Modal size="lg" show={showModal} onHide={() => { onClose(false); setValidated(false); resetForm() }}>
        {/* <LoadingOverlay loading={loading} /> */}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Alert
                type="error"
                show={showErrorToast}
                onClose={handleErrorClose}
                message={ErrorMessage}
              />

              <Alert
                show={showToast}
                onClose={handleClose}
                message={"Kickoff meeting scheduled successfully!!"}
              />
              <div className="modalTitle">
                <Audit style={{ width: "25px", height: "25px" }} />
                &nbsp;Kickoff Meeting
              </div>
            </Modal.Title>
          </Modal.Header>
          <br />
          <Modal.Body style={{ paddingLeft: "50px" }}>
            <Row className={styles.rows}>
              <Col xs={12} md={2} className={styles.loginLabel}>
                <Form.Label className="gap-0">
                  Subject<span className="mandatory">*</span>
                </Form.Label>
              </Col>
              <Col xs={12} md={8}>
                <TextInput
                  value={subject}
                  type="text"
                  name="subject"
                  id="subject"
                  required
                  onChange={(e: any) => {
                    handleChange(e);
                  }}
                  isInvalid={validated && !subject}
                  errrorMessage={(validated && !subject) ? "Please fill this field" : ""}
                />
              </Col>
            </Row>
            <Row className={styles.rows}>
              <Col xs={12} md={2} className={styles.loginLabel}>
                <Form.Label className="gap-0">
                  Kickoff Date<span className="mandatory">*</span>
                </Form.Label>
              </Col>
              <Col xs={12} md={3}>
                <TextInput
                  value={kickoffDate}
                  type="date"
                  name="toDate"
                  id="toDate"
                  required
                  isInvalid={validated && !kickoffDate}
                  errrorMessage={(validated && !kickoffDate) ? "Please fill this field" : ""}
                  min={now.format("YYYY-MM-DD")}
                  onChange={(e: any) => {
                    setKickoffDate(e.currentTarget.value);
                  }}
                />
              </Col>
              <Col xs={12} md={5}>
                <MeetingTimePicker
                  interval={30}
                  onChange={(from, to) => {
                    setFromTime(from);
                    setToTime(to);
                  }}
                />
              </Col>
            </Row>
            <Row className={styles.rows}>
              <Col xs={12} md={2} className={styles.loginLabel}>
                <Form.Label>Message&nbsp;</Form.Label>
              </Col>
              <Col xs={12} md={8}>
                <Form.Control
                  name="body"
                  id="body"
                  as="textarea"
                  rows={5}
                  value={messageBody}
                  onChange={(e: any) => {
                    setMessageBody(e.currentTarget.value);
                  }}
                  className={styles.textArea}
                />
              </Col>
            </Row>

            <Row className={styles.rows}>
              <Col xs={12} md={2} className={styles.loginLabel}>
                <Form.Label>&nbsp;</Form.Label>
              </Col>
              <Col
                xs={12}
                md={8}
                style={{ textAlign: "right", paddingTop: "10px" }}
              >
                <SubmitButton type="secondary" onClick={onCloseClick}>
                  Close
                </SubmitButton>
                &nbsp;&nbsp;
                <SubmitButton disabled={validated} className={styles.sendInvite}>Send Invite{showToast && <Tick />}
                  {loading && (
                    <Spinner
                      animation="border"
                      variant="light"
                      className={styles.submitLoader}
                    />
                  )} </SubmitButton>
              </Col>
            </Row>
          </Modal.Body>
        </Form>
        <br />
      </Modal>
    </>
  );
};

export default SendKickoff;
